/**
 * DNS Migration Wizard
 *
 * Guides a customer through moving their apex domain's DNS to Cloudflare so
 * Tyashin can serve the root (apex) URL via CNAME-flattening. The backend
 * (/dns-migration/*) is a state machine; this page renders one of 4 steps
 * based on the current migration.state and drives it forward with API calls.
 *
 * Flow:
 *   idle → token_validated → zone_ready → records_imported →
 *   tyashin_records_added → waiting_for_ns → zone_activating →
 *   hostname_registering → done
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  Loader2,
  PartyPopper,
  RefreshCw,
  ShieldCheck,
  X,
} from 'lucide-react';

import { apiHelpers } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { apiErrorMessage } from '@/lib/conflict';
import type { DnsMigration, DnsMigrationState } from '@/types';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LoadingPage } from '@/components/ui/loading-spinner';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { cn } from '@/lib/utils';

// Map a migration.state to a 1..4 step index.
function stateToStep(state: DnsMigrationState | undefined): 1 | 2 | 3 | 4 {
  switch (state) {
    case 'idle':
    case 'token_validated':
      return 1;
    case 'zone_ready':
    case 'records_imported':
      return 2;
    case 'tyashin_records_added':
    case 'waiting_for_ns':
      return 3;
    case 'zone_activating':
    case 'hostname_registering':
    case 'done':
      return 4;
    default:
      return 1;
  }
}

// ─── Page ─────────────────────────────────────────────────────────────────

export default function DnsMigrationWizard() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const preDomain = searchParams.get('domain') ?? '';
  const initialMigrationId = searchParams.get('migrationId');

  const [migrationId, setMigrationId] = useState<string | null>(initialMigrationId);
  const [localError, setLocalError] = useState<string | null>(null);
  const [showAbortConfirm, setShowAbortConfirm] = useState(false);

  // Single source of truth for the migration state machine. Step actions
  // return the fresh migration document, which we write straight into this
  // cache entry (setMigration below); polling keeps it advancing while we
  // wait on the registrar. The backend runs its own check inside GET
  // /status, so every poll here can also advance the state server-side.
  const migrationQuery = useQuery({
    queryKey: qk.dnsMigration(projectId),
    queryFn: async () => {
      const res = await apiHelpers.getDnsMigrationStatus(projectId!, migrationId!);
      return (res.data.data ?? null) as DnsMigration | null;
    },
    enabled: Boolean(projectId && migrationId),
    // Poll while we're waiting for registrar NS to flip / the zone to
    // activate — same 10 s cadence as the old setInterval.
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state === 'waiting_for_ns' || state === 'zone_activating' ? 10_000 : false;
    },
  });

  const migration = migrationQuery.data ?? null;

  const setMigration = (m: DnsMigration) => {
    queryClient.setQueryData(qk.dnsMigration(projectId), m);
  };

  // Apex shown in the header — prefer the loaded migration's apex, fall back
  // to the query param while we're still on step 1 without a migration id.
  const apex = migration?.apex || preDomain;

  const step = stateToStep(migration?.state);
  const terminal = migration?.state === 'done' || migration?.state === 'aborted';

  const dismissError = () => setLocalError(null);

  const abortMutation = useMutation({
    mutationFn: async () => {
      const res = await apiHelpers.abortDnsMigration(projectId!, migrationId!);
      return res.data.data as DnsMigration | undefined;
    },
    onSuccess: (m) => {
      if (m) setMigration(m);
      toast.success('Migration aborted');
      setShowAbortConfirm(false);
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Failed to abort migration'));
    },
  });
  const abortPending = abortMutation.isPending;

  const runAbort = () => {
    if (!projectId || !migrationId) return;
    abortMutation.mutate();
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  // Skeleton only while resuming a migration we know nothing about yet —
  // background refetches keep rendering the data we already have.
  if (migrationId && migrationQuery.isPending) return <LoadingPage />;

  if (migrationQuery.isError && migrationQuery.data === undefined) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
          <p className="mb-2 font-semibold">Couldn't load the migration</p>
          <p>{apiErrorMessage(migrationQuery.error, 'Failed to load migration')}</p>
          <Button
            variant="outline"
            className="mt-3"
            onClick={() => navigate(`/projects/${projectId}`)}
          >
            Back to project
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-6">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/projects/${projectId}`)}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold">Migrate DNS to Cloudflare</h1>
          <p className="text-muted-foreground text-sm">
            {apex ? (
              <>
                Domain: <code className="bg-muted rounded px-1">{apex}</code>
              </>
            ) : (
              'Let\u2019s connect your domain'
            )}
          </p>
        </div>
        <Badge variant="outline">Step {step} of 4</Badge>
        {!terminal && migration && (
          <Button
            variant="outline"
            size="sm"
            className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950/30"
            onClick={() => setShowAbortConfirm(true)}
          >
            Abort
          </Button>
        )}
      </div>

      {/* Progress indicator */}
      <StepPills current={step} />

      {/* Last-error banner from backend */}
      {migration?.lastError && migration.state !== 'failed' && (
        <ErrorBanner
          message={migration.lastError}
          onDismiss={dismissError}
          onRetry={() => {
            // Retrying = re-running the current step's primary action. The
            // step components own those actions; we just clear the local flag
            // so users aren't stuck on a stale message if they click Retry.
            dismissError();
          }}
        />
      )}
      {localError && (
        <ErrorBanner message={localError} onDismiss={dismissError} onRetry={dismissError} />
      )}

      {/* Failed state */}
      {migration?.state === 'failed' && (
        <FailedPanel
          message={migration.lastError || 'Migration failed'}
          pending={abortPending}
          onAbort={runAbort}
        />
      )}

      {/* Active step body */}
      <div className="mt-6">
        {step === 1 && (
          <Step1Connect
            projectId={projectId!}
            preDomain={apex}
            migration={migration}
            onMigration={(m) => {
              setMigration(m);
              setMigrationId(m._id);
            }}
            onLocalError={setLocalError}
          />
        )}
        {step === 2 && migration && (
          <Step2Import
            projectId={projectId!}
            migration={migration}
            onMigration={setMigration}
            onLocalError={setLocalError}
          />
        )}
        {step === 3 && migration && (
          <Step3Nameservers migration={migration} onCheckNow={() => migrationQuery.refetch()} />
        )}
        {step === 4 && migration && (
          <Step4Finalize
            projectId={projectId!}
            migration={migration}
            onMigration={setMigration}
            onLocalError={setLocalError}
            onBackToProject={() => navigate(`/projects/${projectId}`)}
          />
        )}
      </div>

      <ConfirmDialog
        open={showAbortConfirm}
        danger
        title="Abort migration?"
        message="This stops the wizard. Your registrar nameservers will NOT be changed back automatically — if you've already updated them, your site may be affected."
        confirmLabel="Abort migration"
        cancelLabel="Keep going"
        isPending={abortPending}
        onConfirm={runAbort}
        onCancel={() => setShowAbortConfirm(false)}
      />
    </div>
  );
}

// ─── Step pills ────────────────────────────────────────────────────────────

function StepPills({ current }: { current: 1 | 2 | 3 | 4 }) {
  const labels = ['Connect Cloudflare', 'Import DNS', 'Update nameservers', 'Register'];
  return (
    <div className="flex items-center gap-2">
      {labels.map((label, idx) => {
        const n = (idx + 1) as 1 | 2 | 3 | 4;
        const active = n === current;
        const done = n < current;
        return (
          <div key={label} className="flex items-center gap-2">
            <div
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold',
                done && 'bg-green-600 text-white',
                active && 'bg-primary text-primary-foreground',
                !done && !active && 'bg-muted text-muted-foreground'
              )}
            >
              {done ? <Check className="h-3.5 w-3.5" /> : n}
            </div>
            <span className={cn('text-xs', active ? 'font-semibold' : 'text-muted-foreground')}>
              {label}
            </span>
            {idx < labels.length - 1 && <span className="text-muted-foreground mx-1">/</span>}
          </div>
        );
      })}
    </div>
  );
}

// ─── Shared banners ────────────────────────────────────────────────────────

function ErrorBanner({
  message,
  onDismiss,
  onRetry,
}: {
  message: string;
  onDismiss: () => void;
  onRetry: () => void;
}) {
  return (
    <div className="mt-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <p className="font-medium">Something went wrong</p>
        <p className="mt-0.5 opacity-90">{message}</p>
      </div>
      <div className="flex gap-2">
        <button
          onClick={onRetry}
          className="rounded px-2 py-1 text-xs font-medium hover:bg-red-100 dark:hover:bg-red-900/30"
        >
          Retry
        </button>
        <button
          onClick={onDismiss}
          className="rounded p-1 hover:bg-red-100 dark:hover:bg-red-900/30"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function FailedPanel({
  message,
  pending,
  onAbort,
}: {
  message: string;
  pending: boolean;
  onAbort: () => void;
}) {
  return (
    <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm dark:border-red-900 dark:bg-red-950/30">
      <div className="mb-2 flex items-center gap-2 font-semibold text-red-800 dark:text-red-300">
        <AlertCircle className="h-4 w-4" /> Migration failed
      </div>
      <p className="text-red-800/90 dark:text-red-300/90">{message}</p>
      <div className="mt-3">
        <Button variant="destructive" size="sm" disabled={pending} onClick={onAbort}>
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Abort and restart'}
        </Button>
      </div>
    </div>
  );
}

// ─── Copyable pill ─────────────────────────────────────────────────────────

function CopyPill({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* ignore */
        }
      }}
      className="bg-muted hover:bg-muted/70 inline-flex items-center gap-2 rounded-md border px-3 py-1.5 font-mono text-sm"
    >
      <span>{value}</span>
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-600" />
      ) : (
        <Copy className="text-muted-foreground h-3.5 w-3.5" />
      )}
    </button>
  );
}

// ─── Step 1: Connect Cloudflare ────────────────────────────────────────────

function Step1Connect({
  projectId,
  preDomain,
  migration,
  onMigration,
  onLocalError,
}: {
  projectId: string;
  preDomain: string;
  migration: DnsMigration | null;
  onMigration: (m: DnsMigration) => void;
  onLocalError: (msg: string | null) => void;
}) {
  const [domain, setDomain] = useState(preDomain);
  const [token, setToken] = useState('');
  const [accounts, setAccounts] = useState<{ id: string; name: string }[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('');

  useEffect(() => {
    setDomain(preDomain);
  }, [preDomain]);

  const tokenValidated = migration?.state === 'token_validated';

  const startMutation = useMutation({
    mutationFn: async () => {
      const res = await apiHelpers.startDnsMigration(projectId, {
        domain: domain.trim(),
        token: token.trim(),
      });
      return res.data.data;
    },
    onSuccess: (data) => {
      if (data) {
        onMigration(data.migration);
        setAccounts(data.accounts || []);
        if (data.accounts?.length === 1) setSelectedAccount(data.accounts[0].id);
      }
    },
    onError: (err) => {
      onLocalError(apiErrorMessage(err, 'Failed to validate token'));
    },
  });
  const submitting = startMutation.isPending;

  const handleStart = () => {
    onLocalError(null);
    if (!domain.trim() || !token.trim()) {
      onLocalError('Please paste your Cloudflare API token and confirm the domain.');
      return;
    }
    startMutation.mutate();
  };

  const accountMutation = useMutation({
    mutationFn: async () => {
      const picked = accounts.find((a) => a.id === selectedAccount);
      const res = await apiHelpers.chooseDnsMigrationAccount(projectId, migration!._id, {
        accountId: selectedAccount,
        accountName: picked?.name,
      });
      return res.data.data as DnsMigration | undefined;
    },
    onSuccess: (m) => {
      if (m) onMigration(m);
    },
    onError: (err) => {
      onLocalError(apiErrorMessage(err, 'Failed to create CF zone'));
    },
  });
  const accountPending = accountMutation.isPending;

  const handlePickAccount = () => {
    if (!migration || !selectedAccount) return;
    accountMutation.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" /> Connect Cloudflare
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-6 md:grid-cols-2">
          {/* Left: instructions */}
          <div>
            <h3 className="mb-2 text-sm font-semibold">Create a scoped API token</h3>
            <ol className="text-muted-foreground list-decimal space-y-1.5 pl-5 text-sm">
              <li>
                Open{' '}
                <a
                  href="https://dash.cloudflare.com/profile/api-tokens"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary inline-flex items-center gap-0.5 underline hover:no-underline"
                >
                  Cloudflare API Tokens
                  <ExternalLink className="h-3 w-3" />
                </a>
              </li>
              <li>
                Click <strong>Create Token</strong> (use "Create Custom Token").
              </li>
              <li>Add the permissions below and include all accounts + zones.</li>
              <li>Copy the token once — Cloudflare only shows it here.</li>
            </ol>
            <div className="bg-muted/50 mt-4 rounded-md border p-3 text-xs">
              <p className="mb-1.5 font-semibold">Required permissions</p>
              <ul className="space-y-0.5 font-mono">
                <li>Zone : Read</li>
                <li>Zone : Edit</li>
                <li>DNS : Edit</li>
                <li>Account : Read</li>
                <li>Zone Settings : Edit</li>
              </ul>
            </div>
            <p className="text-muted-foreground mt-3 text-xs">
              The token is encrypted on our side and only used for this migration.
            </p>
          </div>

          {/* Right: token input OR account picker */}
          <div>
            {!tokenValidated ? (
              <>
                <label className="mb-1 block text-sm font-medium">Apex domain</label>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="example.com"
                  disabled={submitting || Boolean(migration)}
                  className="border-input focus-visible:ring-ring mb-4 flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 disabled:opacity-50"
                />
                <label className="mb-1 block text-sm font-medium">Cloudflare API token</label>
                <Textarea
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Paste your token here"
                  rows={4}
                  disabled={submitting}
                  className="font-mono text-xs"
                />
                <Button className="mt-3 w-full" onClick={handleStart} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Validating
                    </>
                  ) : (
                    <>
                      Continue <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                <h3 className="mb-2 text-sm font-semibold">Pick an account</h3>
                <p className="text-muted-foreground mb-3 text-xs">
                  Cloudflare will create the zone inside this account.
                </p>
                <select
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  disabled={accountPending}
                  className="border-input mb-3 flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm disabled:opacity-50"
                >
                  <option value="" disabled>
                    Select an account…
                  </option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.id.slice(0, 8)}…)
                    </option>
                  ))}
                </select>
                <Button
                  className="w-full"
                  onClick={handlePickAccount}
                  disabled={accountPending || !selectedAccount}
                >
                  {accountPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Creating zone
                    </>
                  ) : (
                    <>
                      Use this account <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Step 2: Import DNS ───────────────────────────────────────────────────

function Step2Import({
  projectId,
  migration,
  onMigration,
  onLocalError,
}: {
  projectId: string;
  migration: DnsMigration;
  onMigration: (m: DnsMigration) => void;
  onLocalError: (msg: string | null) => void;
}) {
  const zone = migration.zone;
  const autoImported = zone?.autoImported === true;
  const importedCount = zone?.importedRecordCount ?? 0;

  const rescanMutation = useMutation({
    mutationFn: async () => {
      const res = await apiHelpers.importDnsMigrationRecords(projectId, migration._id);
      return res.data.data as DnsMigration | undefined;
    },
    onSuccess: (m) => {
      if (m) onMigration(m);
      toast.success('DNS records re-scanned');
    },
    onError: (err) => {
      onLocalError(apiErrorMessage(err, 'Re-scan failed'));
    },
  });
  const rescanning = rescanMutation.isPending;

  const handleRescan = () => {
    onLocalError(null);
    rescanMutation.mutate();
  };

  const continueMutation = useMutation({
    mutationFn: async () => {
      const res = await apiHelpers.addDnsMigrationRecords(projectId, migration._id);
      return res.data.data as DnsMigration | undefined;
    },
    onSuccess: (m) => {
      if (m) onMigration(m);
    },
    onError: (err) => {
      onLocalError(apiErrorMessage(err, 'Failed to add Tyashin records'));
    },
  });
  const continuing = continueMutation.isPending;

  const handleContinue = () => {
    onLocalError(null);
    continueMutation.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Import your DNS</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300">
          Cloudflare automatically scanned your existing DNS records from your current nameservers.
          Nothing is lost — your MX, TXT, existing subdomains and any other records are preserved
          as-is.
        </div>

        <div>
          <div className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
            Records imported
          </div>
          <div className="mt-1 flex items-center gap-2">
            <Badge variant={autoImported ? 'default' : 'secondary'}>
              {importedCount} record{importedCount === 1 ? '' : 's'}
            </Badge>
            {autoImported ? (
              <span className="text-xs text-green-700 dark:text-green-400">
                Auto-imported successfully
              </span>
            ) : (
              <span className="text-xs text-amber-700 dark:text-amber-400">
                Auto-import didn't run — click below to re-scan
              </span>
            )}
          </div>
        </div>

        {zone?.nameServers && zone.nameServers.length > 0 && (
          <div>
            <div className="text-muted-foreground mb-2 text-xs font-medium uppercase tracking-wide">
              Your new Cloudflare nameservers (you'll set these at your registrar in step 3)
            </div>
            <div className="flex flex-wrap gap-2">
              {zone.nameServers.map((ns) => (
                <CopyPill key={ns} value={ns} />
              ))}
            </div>
          </div>
        )}

        {!autoImported && (
          <Button variant="outline" onClick={handleRescan} disabled={rescanning}>
            {rescanning ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Re-scanning
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" /> Re-scan DNS records
              </>
            )}
          </Button>
        )}

        <div className="pt-2">
          <Button onClick={handleContinue} disabled={continuing}>
            {continuing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Adding Tyashin records
              </>
            ) : (
              <>
                Continue <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Step 3: Update nameservers ────────────────────────────────────────────

function Step3Nameservers({
  migration,
  onCheckNow,
}: {
  migration: DnsMigration;
  onCheckNow: () => Promise<unknown>;
}) {
  const ns = migration.zone?.nameServers || [];
  const prog = migration.nsProgress;

  // One-off "check now" button for impatient users. Polling runs every 10s
  // upstream (refetchInterval on the migration query), but a manual nudge is
  // nice — this just forces an immediate refetch of the same query.
  const [checking, setChecking] = useState(false);
  const handleCheck = async () => {
    setChecking(true);
    try {
      await onCheckNow();
    } finally {
      setChecking(false);
    }
  };

  const matched = prog?.matched ?? 0;
  const expected = prog?.expected ?? ns.length ?? 2;
  const pct = expected > 0 ? Math.min(100, Math.round((matched / expected) * 100)) : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Update nameservers at your registrar</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm">
          Point <code className="bg-muted rounded px-1">{migration.apex}</code> at Cloudflare by
          replacing your registrar's nameservers with the two below. DNS propagation usually takes a
          few minutes but can take up to a few hours.
        </p>

        <div>
          <div className="text-muted-foreground mb-2 text-xs font-medium uppercase tracking-wide">
            Set these as your nameservers
          </div>
          <div className="flex flex-wrap gap-2">
            {ns.map((n) => (
              <CopyPill key={n} value={n} />
            ))}
          </div>
        </div>

        <ol className="text-muted-foreground list-decimal space-y-1.5 pl-5 text-sm">
          <li>Log into your domain registrar (where you bought the domain).</li>
          <li>Find the DNS / Nameservers section for {migration.apex}.</li>
          <li>Replace the current nameservers with the Cloudflare pair above.</li>
          <li>Save. Come back here — we'll detect the change automatically.</li>
        </ol>

        <details className="rounded-md border p-3 text-sm">
          <summary className="cursor-pointer font-medium">Hostinger-specific steps</summary>
          <ol className="text-muted-foreground mt-2 list-decimal space-y-1 pl-5 text-xs">
            <li>
              Hostinger dashboard → <strong>Domains</strong>.
            </li>
            <li>
              Click <strong>Manage</strong> on {migration.apex}.
            </li>
            <li>
              Go to <strong>DNS / Nameservers</strong>.
            </li>
            <li>
              Click <strong>Change nameservers</strong>.
            </li>
            <li>Select "Use custom nameservers" and paste the CF pair above.</li>
            <li>
              Click <strong>Save</strong>.
            </li>
          </ol>
        </details>

        {/* Progress */}
        <div className="rounded-md border p-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-medium">
              Detected nameservers
              <span className="text-muted-foreground ml-2 text-xs">
                {matched} / {expected} match
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={handleCheck} disabled={checking}>
              {checking ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Check now
            </Button>
          </div>
          <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
            <div className="h-full bg-green-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          {prog?.registrarNameServers && prog.registrarNameServers.length > 0 ? (
            <div className="mt-3 space-y-1 text-xs">
              <div className="text-muted-foreground">Currently at registrar:</div>
              <ul className="font-mono">
                {prog.registrarNameServers.map((r) => (
                  <li key={r} className="flex items-center gap-1.5">
                    {ns.some((x) => x.toLowerCase() === r.toLowerCase()) ? (
                      <CheckCircle2 className="h-3 w-3 text-green-600" />
                    ) : (
                      <span className="inline-block h-3 w-3 rounded-full bg-amber-400" />
                    )}
                    {r}
                  </li>
                ))}
              </ul>
              {prog.lastCheckedAt && (
                <div className="text-muted-foreground mt-1 text-[10px]">
                  Last checked {new Date(prog.lastCheckedAt).toLocaleTimeString()}
                </div>
              )}
            </div>
          ) : (
            <div className="text-muted-foreground mt-3 text-xs">
              Waiting for the first check… we poll every 10 seconds.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Step 4: Register with Tyashin ────────────────────────────────────────

function Step4Finalize({
  projectId,
  migration,
  onMigration,
  onLocalError,
  onBackToProject,
}: {
  projectId: string;
  migration: DnsMigration;
  onMigration: (m: DnsMigration) => void;
  onLocalError: (msg: string | null) => void;
  onBackToProject: () => void;
}) {
  const done = migration.state === 'done';

  const apexUrl = `https://${migration.apex}`;
  const wwwUrl = `https://www.${migration.apex}`;

  const showApex = migration.hostnameMode !== 'www';
  const showWww = migration.hostnameMode !== 'apex';

  const finalizeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiHelpers.finalizeDnsMigration(projectId, migration._id);
      return res.data.data as DnsMigration | undefined;
    },
    onSuccess: (m) => {
      if (m) onMigration(m);
    },
    onError: (err) => {
      onLocalError(apiErrorMessage(err, 'Failed to finalize'));
    },
  });
  const finalizing = finalizeMutation.isPending;

  const handleFinalize = () => {
    onLocalError(null);
    finalizeMutation.mutate();
  };

  if (done) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/40">
            <PartyPopper className="h-7 w-7 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">You're live on your own domain</h2>
            <p className="text-muted-foreground mt-1 text-sm">
              Cloudflare is now serving {migration.apex}. SSL is provisioned automatically.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {showApex && (
              <a
                href={apexUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:bg-accent inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm"
              >
                {apexUrl} <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            {showWww && (
              <a
                href={wwwUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:bg-accent inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm"
              >
                {wwwUrl} <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
          <Button onClick={onBackToProject}>Back to Domains</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Register with Tyashin</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm">
          Cloudflare is now authoritative for {migration.apex}. One more step: we'll register the
          hostname with Tyashin so SSL and routing are provisioned.
        </p>
        <Button onClick={handleFinalize} disabled={finalizing}>
          {finalizing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Finishing
            </>
          ) : (
            <>
              Finish migration <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
